'use client';

import { Activity, Brain, Route } from 'lucide-react';
import Image from 'next/image';

type AgentPhase = 'thinking' | 'planning' | 'working';

interface AgentStatusIndicatorProps {
  status: string;
  label: string;
  compact?: boolean;
}

const PHASES = ['thinking', 'planning', 'working'] as const;

const PHASE_STYLES: Record<
  AgentPhase,
  {
    icon: string;
    dot: string;
    line: string;
    logo?: string;
  }
> = {
  thinking: {
    icon: 'text-[#f98715]',
    dot: 'bg-[#f98715]',
    line: 'before:bg-[#f98715]/30',
    logo: '/agent-status/thinking.gif',
  },
  planning: {
    icon: 'text-[#45b7d1]',
    dot: 'bg-[#070400] border border-[#45b7d1]',
    line: 'before:bg-[#45b7d1]/30',
    logo: '/agent-status/planning.gif',
  },
  working: {
    icon: 'text-[#16c784]',
    dot: 'bg-[#16c784]',
    line: 'before:bg-[#16c784]/30',
    logo: '/agent-status/working.gif',
  },
};

function isAgentPhase(status: string): status is AgentPhase {
  return (PHASES as readonly string[]).includes(status);
}

function getPhase(status: string): AgentPhase {
  return isAgentPhase(status) ? status : 'working';
}

function PhaseVisual({ phase }: { phase: AgentPhase }) {
  const styles = PHASE_STYLES[phase];

  if (phase === 'planning') {
    return (
      <span
        className={`relative inline-flex w-9 flex-none items-center justify-between before:absolute before:left-1 before:right-1 before:top-1/2 before:h-px before:-translate-y-1/2 before:content-[''] ${styles.line}`}
        aria-hidden="true">
        <span className={`relative z-10 h-2 w-2 rounded-full motion-safe:animate-bounce ${styles.dot}`} />
        <span className={`relative z-10 h-2 w-2 rounded-full motion-safe:animate-bounce [animation-delay:150ms] ${styles.dot}`} />
        <span className={`relative z-10 h-2 w-2 rounded-full motion-safe:animate-bounce [animation-delay:300ms] ${styles.dot}`} />
      </span>
    );
  }

  if (phase === 'working') {
    return (
      <span
        className="inline-flex w-9 flex-none items-end justify-center gap-1"
        aria-hidden="true">
        <span className={`h-2.5 w-1 rounded-full motion-safe:animate-pulse ${styles.dot}`} />
        <span className={`h-4 w-1 rounded-full motion-safe:animate-pulse [animation-delay:120ms] ${styles.dot}`} />
        <span className={`h-3 w-1 rounded-full motion-safe:animate-pulse [animation-delay:240ms] ${styles.dot}`} />
      </span>
    );
  }

  return (
    <span
      className="inline-flex w-9 flex-none items-center justify-center gap-1"
      aria-hidden="true">
      <span className={`h-1.5 w-1.5 rounded-full motion-safe:animate-pulse ${styles.dot}`} />
      <span className={`h-1.5 w-1.5 rounded-full motion-safe:animate-pulse [animation-delay:150ms] ${styles.dot}`} />
      <span className={`h-1.5 w-1.5 rounded-full motion-safe:animate-pulse [animation-delay:300ms] ${styles.dot}`} />
    </span>
  );
}

export function AgentStatusIndicator({ status, label, compact = false }: AgentStatusIndicatorProps) {
  const phase = getPhase(status);
  const Icon = phase === 'thinking' ? Brain : phase === 'planning' ? Route : Activity;
  const styles = PHASE_STYLES[phase];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`inline-flex max-w-full items-center text-white align-middle ${
        compact ? 'min-h-7 gap-2 px-2 py-1' : 'min-h-9 gap-2.5 px-2.5 py-1.5'
      }`}>
      {styles.logo && (
        <Image
          src={styles.logo}
          alt=""
          width={28}
          height={28}
          unoptimized
          aria-hidden="true"
          className={`flex-none rounded-md object-cover ${compact ? 'size-6' : 'size-7'}`}
        />
      )}
      {!styles.logo && (
        <>
          <span
            className={`inline-flex items-center justify-center ${styles.icon}`}
            aria-hidden="true">
            <Icon className="size-4" />
          </span>
          <PhaseVisual phase={phase} />
        </>
      )}
      <span className={`min-w-0 break-words text-white/85 ${compact ? 'text-xs leading-4' : 'text-sm leading-5'}`}>{label}</span>
    </div>
  );
}
